import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './Blog.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://sbg-backend.onrender.com';

const BlogPost = () => {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        // Check if it's a mock post ID
        if (id.startsWith('mock-')) {
          // Use mock data for development
          const mockPost = getMockPost(id);
          if (mockPost) {
            setPost(mockPost);
          } else {
            setError('Blog post not found');
          }
        } else {
          // Fetch the blog post from the API
          const response = await axios.get(`${API_URL}/api/blog/${id}`);
          setPost(response.data);
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError('Unable to load the blog post. Please try again later.');
        
        // Try to use mock data as fallback
        const mockPost = getMockPost(id);
        if (mockPost) {
          setPost(mockPost);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  // Helper function to get mock posts for development
  const getMockPost = (mockId) => {
    const mockPosts = {
      'mock-1': {
        _id: 'mock-1',
        title: 'Top Recruiting Strategies for 2024',
        content: `<h2>Introduction</h2>
        <p>Recruiting top talent has become increasingly competitive in 2024. Companies need to adapt their strategies to attract the <strong>best candidates</strong> in a changing job market. With unemployment rates at historic lows, organizations must be creative and proactive in their approach.</p>
        
        <h2>Key Strategies</h2>
        <p>Successful recruiting in today's market requires a multi-faceted approach that combines technology with human connection. Here are some proven strategies that can help your organization stand out:</p>
        
        <h3>Leverage AI-Powered Recruiting Tools</h3>
        <p>Artificial intelligence can help streamline your recruiting process by automatically screening resumes and identifying top candidates. These tools can:</p>
        <ul>
          <li><strong>Save time</strong> by automating repetitive tasks</li>
          <li>Reduce bias in the screening process</li>
          <li>Identify candidates who might be overlooked by traditional methods</li>
          <li>Provide data-driven insights to improve your recruiting strategy</li>
        </ul>
        
        <h3>Focus on Employer Branding</h3>
        <p>Your company's reputation as an employer is crucial for attracting quality candidates. Invest in building a positive employer brand by:</p>
        <ul>
          <li>Showcasing your company culture on social media</li>
          <li>Encouraging employees to share their experiences</li>
          <li>Highlighting benefits and growth opportunities</li>
          <li><strong>Responding professionally</strong> to reviews on job sites</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>By implementing these strategies, you can improve your recruiting outcomes and build a stronger team for your organization. Remember that the most successful recruiting approaches combine technological innovation with a human touch.</p>
        
        <p>Partner with a staffing agency like <strong>Scott Business Group</strong> to leverage industry expertise and expand your reach to passive candidates who might be the perfect fit for your organization.</p>`,
        excerpt: 'Discover the most effective recruiting strategies for finding top talent in 2024.',
        author: 'Scott Business Group',
        createdAt: new Date().toISOString(),
        publishedAt: new Date().toISOString(),
        isPublished: true,
        tags: ['recruiting', 'hiring', 'strategies']
      },
      'mock-2': {
        _id: 'mock-2',
        title: 'How to Prepare for a Technical Interview',
        content: `<h2>Introduction</h2>
        <p>Technical interviews can be intimidating, but with the right preparation, you can showcase your skills and land your dream job. Whether you're interviewing for a position in software development, data science, or engineering, these tips will help you approach your interview with <strong>confidence and competence</strong>.</p>
        
        <h2>Preparation Tips</h2>
        <p>Successful technical interviews require both technical knowledge and soft skills. Here's how to prepare effectively:</p>
        
        <h3>Review Fundamentals</h3>
        <p>Make sure you have a solid understanding of the core concepts in your field. Review data structures, algorithms, and common problem-solving techniques. Focus on:</p>
        <ul>
          <li><strong>Data structures</strong> (arrays, linked lists, trees, graphs, hash tables)</li>
          <li><strong>Algorithms</strong> (sorting, searching, dynamic programming)</li>
          <li>Time and space complexity analysis</li>
          <li>System design principles for senior roles</li>
        </ul>
        
        <h3>Practice Mock Interviews</h3>
        <p>Conduct practice interviews with friends or use online platforms that simulate technical interviews. This will help you:</p>
        <ol>
          <li>Get comfortable explaining your thought process out loud</li>
          <li>Improve your problem-solving speed</li>
          <li>Receive feedback on areas for improvement</li>
          <li>Reduce interview anxiety through familiarity</li>
        </ol>
        
        <h3>Research the Company</h3>
        <p>Understanding the company's products, technologies, and culture can give you an edge in the interview. Be prepared to discuss:</p>
        <ul>
          <li>How your skills align with their tech stack</li>
          <li>Relevant projects you've worked on</li>
          <li>How you can contribute to their specific challenges</li>
        </ul>
        
        <h2>Conclusion</h2>
        <p>With thorough preparation and practice, you can approach your technical interview with confidence and increase your chances of success. Remember to <strong>stay calm</strong>, communicate clearly, and demonstrate not just your technical abilities but also your problem-solving approach.</p>
        
        <p>Working with a specialized recruiter from <strong>Scott Business Group</strong> can help you prepare specifically for the companies and roles you're targeting, giving you valuable insights into what employers are looking for.</p>`,
        excerpt: 'Learn how to ace your next technical interview with these proven preparation tips.',
        author: 'Scott Business Group',
        createdAt: new Date(Date.now() - 86400000).toISOString(),
        publishedAt: new Date(Date.now() - 86400000).toISOString(),
        isPublished: true,
        tags: ['interview', 'technical', 'preparation']
      }
    };
    
    return mockPosts[mockId];
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="bg-green-50 min-h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border-l-4 border-orange-500 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-orange-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error || 'Blog post not found'}</p>
              </div>
            </div>
          </div>
          <Link to="/blog" className="text-orange-600 hover:text-orange-700 inline-flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-green-50 min-h-screen py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" className="text-orange-600 hover:text-orange-700 mb-6 inline-flex items-center">
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Blog
        </Link>
        
        <article className="bg-white rounded-lg shadow-md overflow-hidden border-l-4 border-orange-500">
          {post.coverImage && (
            <div className="relative">
              <img
                src={post.coverImage}
                alt={post.title}
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-green-900/60 to-transparent"></div>
            </div>
          )}
          
          <div className="p-8">
            <h1 className="text-3xl font-bold text-green-800 mb-4">
              {post.title}
            </h1>
            
            <div className="flex items-center text-sm text-green-700 mb-6">
              <span className="mr-4 font-medium">{post.author}</span>
              <span className="bg-orange-100 px-3 py-1 rounded-full text-orange-800">
                {post.publishedAt && new Date(post.publishedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
            
            {post.tags && post.tags.length > 0 && (
              <div className="mb-8">
                {post.tags.map(tag => (
                  <span 
                    key={tag} 
                    className="inline-block bg-green-100 rounded-full px-3 py-1 text-sm font-semibold text-green-800 mr-2 mb-2"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
            
            <div 
              className="blog-content prose prose-green max-w-none"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          </div>
        </article>
      </div>
    </div>
  );
};

export default BlogPost; 